import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import axios from "axios";
import React, { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import SubscriptionCommisionsTable from "./SubscriptionCommisionsTable";
import { axiosConfig } from "../../../axios/axiosConfig";

const SubscriptionCommissions = () => {
  const [plans, setPlans] = useState([]);
  const [planLoading, setPlanLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [form, setForm] = useState({
    subscriptionplan: "",
    commision_amount: "",
    status: "active",
  });
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [refresh, setRefresh] = useState(false);
  useEffect(() => {
    const fetchPlans = async () => {
      setPlanLoading(true);
      try {
        const res = await axiosConfig({
          method: "get",
          url: "/subscription/subscription_plan",
        });
        setPlans(res?.data?.response?.plans || []);
      } catch (err) {
        toast.error("Failed to load plans");
      } finally {
        setPlanLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handlePlanChange = (e) => {
    const planId = e.target.value;
    setSelectedPlanId(planId);
    setForm((prev) => ({
      ...prev,
      subscriptionplan: planId,
    }));
  };

  const handleSubmit = async () => {
    console.log(form, "form");
    const { subscriptionplan, commision_amount, status } = form;
    if (!subscriptionplan || !commision_amount || !status) {
      toast.error("Please fill all fields");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        subscriptionplan: Number(subscriptionplan),
        commision_amount: Number(commision_amount),
        status,
      };
      if (editId) {
        // PUT REQUEST
        await axiosConfig({
          method: "put",
          url: `/subscription/subscription-commisions/${editId}/`,
          data: payload,
        });
        toast.success("Commission updated successfully!");
      } else {
        await axiosConfig({
          method: "post",
          url: "/subscription/subscription-commisions/",
          data: payload,
        });
      }
      toast.success("Commission added successfully!");
      setSelectedPlanId("");
      setForm({
        subscriptionplan: "",
        commision_amount: "",
        status: "active",
      });
      setEditId(null);
      setRefresh((prev) => !prev);
    } catch (err) {
      toast.error("Failed to submit");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    console.log(row, "row");
    setSelectedPlanId(row.subscriptionplan);
    setForm({
      subscriptionplan: row.subscriptionplan,
      commision_amount: row.commision_amount,
      status: row.status,
    });
    setEditId(row.id);
  };
  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      <Toaster position="top-right" />

      {/* Page Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 28px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <MonetizationOnIcon sx={{ color: "#fff", fontSize: 32 }} />
        <Box>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}>
            Subscription Commissions
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}>
            Manage commission amounts for subscription plans
          </Typography>
        </Box>
      </Box>

      {/* Form Card */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          mb: 3,
          overflow: "hidden",
        }}
      >
        <Box sx={{ background: "#04343a", p: "12px 20px" }}>
          <Typography sx={{ color: "#fff", fontWeight: 600, fontSize: "14px" }}>
            {editId ? "Edit Commission" : "Add Commission"}
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Select Plan</InputLabel>
              <Select value={selectedPlanId} label="Select Plan" onChange={handlePlanChange}>
                {planLoading ? (
                  <MenuItem disabled>Loading...</MenuItem>
                ) : (
                  plans.map((plan) => {
                    const planId = plan?.data?.[0]?.id;
                    const planName = plan?.plan_name;
                    return (
                      <MenuItem key={planId} value={planId}>
                        {planName}
                      </MenuItem>
                    );
                  })
                )}
              </Select>
            </FormControl>

            {selectedPlanId && (
              <>
                <TextField
                  label="Subscription Plan ID"
                  size="small"
                  sx={{ minWidth: 180 }}
                  value={form.subscriptionplan}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Commission Amount"
                  size="small"
                  type="number"
                  sx={{ minWidth: 180 }}
                  value={form.commision_amount}
                  onChange={(e) => setForm({ ...form, commision_amount: e.target.value })}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    label="Status"
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  sx={{
                    background: "linear-gradient(135deg, #016551, #014d3d)",
                    color: "#fff",
                    fontWeight: 600,
                    borderRadius: "8px",
                    px: 3,
                    py: "9px",
                    boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
                      boxShadow: "0 6px 16px rgba(1,101,81,0.4)",
                    },
                    "&:disabled": { background: "#ccc" },
                  }}
                >
                  {loading ? (
                    <CircularProgress size={16} sx={{ color: "#fff" }} />
                  ) : editId ? (
                    "Update"
                  ) : (
                    "Add Commission"
                  )}
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Table Card */}
      <Box
        sx={{
          background: "#fff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 1px 6px rgba(0,0,0,0.07)",
          overflow: "hidden",
        }}
      >
        <SubscriptionCommisionsTable refresh={refresh} onEdit={handleEdit} />
      </Box>
    </Box>
  );
};

export default SubscriptionCommissions;
